import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { DAY_NAMES, getDateStr, getCurrentWeek, getTotalWeeks, getMonday, hexToRgba, currencyLabel } from './clientUtils';
import { STATUS_COLORS, NEUTRAL, onAccent } from './designTokens';

// T-13: kundens Hem. Nästa tid, "Fortsätt här" (aktivt program), veckans prickar och
// senaste meddelandet från terapeuten. Sidhuvudet ritas av ClientView (ClientHero).

const DAYS_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function ClientHomeTab({
  client,
  bookings,
  programs,
  cancellingBookingId,
  setCancellingBookingId,
  cancellationFeedback,
  cancelMyBooking,
  handleTabChange,
  openProgram,
  setActiveTab,
  weekSessionCounts,
  weekSessionDays = {},
  brandAccent,
  theme,
}) {
  const [latestMsg, setLatestMsg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('messages')
        .select('content, created_at, read')
        .eq('client_id', client.id)
        .eq('therapist_id', client.therapist_id)
        .eq('sender_role', 'therapist')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setLatestMsg(data || null);
    })();
    return () => { cancelled = true; };
  }, [client.id, client.therapist_id]);

  const today = getDateStr(new Date());
  const myUpcoming = bookings
    .filter(b => b.client_id === client.id && b.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
  const nextBooking = myUpcoming[0] || null;
  const nextBookingPriceLabel = nextBooking
    ? (() => {
        const price = nextBooking.campaign_active_snapshot ? nextBooking.campaign_price_snapshot : nextBooking.price_snapshot;
        if (price == null) return null;
        return `${price} ${currencyLabel(nextBooking.currency_snapshot)}`;
      })()
    : null;

  const activeProgram = programs.find(cp => !cp.end_date || cp.end_date >= today) || null;
  const activeCpWeek = activeProgram ? getCurrentWeek(activeProgram) : 0;
  const activeCpTotal = activeProgram ? getTotalWeeks(activeProgram) : null;
  const activeCpSessions = activeProgram ? (weekSessionCounts[activeProgram.program?.id] || 0) : 0;
  const activeCpFreq = activeProgram?.frequency_per_week || 0;
  const activeCpWeekDone = activeCpFreq > 0 && activeCpSessions >= activeCpFreq;
  const activeDays = activeProgram ? (weekSessionDays[activeProgram.program?.id] || []) : [];

  const accentText = onAccent(brandAccent);
  const card = { background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: '14px', boxShadow: theme.card_shadow };
  const sectionLabel = { fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted };

  const dateBadge = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const top = dateStr === today ? 'Idag' : dateStr === getDateStr(tomorrow) ? 'Imorgon' : `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
    return top;
  };

  // Veckans dagar (mån–sön)
  const monday = getMonday(new Date());
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(d.getDate() + i); return d; });

  const fmtMsgTime = (iso) => {
    const d = new Date(iso);
    const ds = getDateStr(d);
    const y = new Date(); y.setDate(y.getDate() - 1);
    const t = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    return ds === today ? `idag ${t}` : ds === getDateStr(y) ? `igår ${t}` : `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {cancellationFeedback && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: STATUS_COLORS.success_bg, border: `1px solid ${STATUS_COLORS.success}40`, borderRadius: '10px', padding: '10px 14px' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={STATUS_COLORS.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          <span style={{ fontSize: '0.82em', color: STATUS_COLORS.success, fontWeight: '500' }}>Din bokning är avbokad</span>
        </div>
      )}

      {/* Nästa tid */}
      {nextBooking ? (
        <div style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '56px', textAlign: 'center', borderRadius: '10px', background: theme.accent_soft_bg, padding: '6px 8px', flexShrink: 0 }}>
            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.accent_soft_text, whiteSpace: 'nowrap' }}>{dateBadge(nextBooking.date)}</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: '800', color: theme.accent_soft_text, lineHeight: 1.1 }}>{nextBooking.start_time?.slice(0, 5)}</div>
          </div>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>{nextBooking.service?.title || 'Bokad tid'}</div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>{[`${DAY_NAMES[new Date(nextBooking.date + 'T12:00:00').getDay()]}`, `${nextBooking.start_time?.slice(0, 5)}–${nextBooking.end_time?.slice(0, 5)}`, nextBookingPriceLabel].filter(Boolean).join(' · ')}</div>
          </div>
          {cancellingBookingId === nextBooking.id ? (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="button" onClick={() => cancelMyBooking(nextBooking.id)} style={{ background: STATUS_COLORS.error_bg, border: `1px solid ${STATUS_COLORS.error}`, color: '#B91C1C', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Ja, avboka</button>
              <button type="button" onClick={() => setCancellingBookingId(null)} style={{ background: 'transparent', border: `1px solid ${theme.card_border}`, color: NEUTRAL.text, padding: '6px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Behåll</button>
            </div>
          ) : (
            <button type="button" onClick={() => setCancellingBookingId(nextBooking.id)} style={{ background: 'transparent', border: `1px solid ${theme.card_border}`, color: NEUTRAL.text, padding: '6px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Avboka</button>
          )}
        </div>
      ) : (
        <div style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>Ingen bokad tid</div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>Boka när det passar dig</div>
          </div>
          <button type="button" onClick={() => handleTabChange('bookings')} style={{ background: brandAccent, border: `1px solid ${brandAccent}`, color: accentText, padding: '8px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Boka tid</button>
        </div>
      )}

      {/* Fortsätt här – aktivt program */}
      {activeProgram ? (
        <div
          onClick={() => { openProgram(activeProgram); setActiveTab('programs'); }}
          style={{ background: brandAccent, color: accentText, borderRadius: '14px', padding: '16px', cursor: 'pointer', boxShadow: `0 6px 18px ${hexToRgba(brandAccent, 0.3)}` }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85 }}>{activeCpWeekDone ? 'Veckan är klar' : 'Fortsätt här'}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '6px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeProgram.program?.title}</div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                {[activeCpWeek > 0 ? `vecka ${activeCpWeek}${activeCpTotal ? ` av ${activeCpTotal}` : ''}` : null, activeCpFreq > 0 ? `${activeCpFreq} pass/vecka` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: accentText === '#FFFFFF' ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>›</div>
          </div>
          {activeCpFreq > 0 && (
            <>
              <div style={{ display: 'flex', gap: '6px', marginTop: '12px' }}>
                {Array.from({ length: activeCpFreq }, (_, i) => (
                  <div key={i} style={{ flex: 1, height: '6px', borderRadius: '999px', background: i < activeCpSessions ? accentText : (accentText === '#FFFFFF' ? 'rgba(255,255,255,0.3)' : 'rgba(15,23,42,0.18)') }} />
                ))}
              </div>
              <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '6px' }}>{activeCpSessions} av {activeCpFreq} pass klara denna vecka</div>
            </>
          )}
        </div>
      ) : (
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>Inget program ännu</div>
          <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>Din terapeut tilldelar ett program efter ert besök.</div>
        </div>
      )}

      {/* Den här veckan */}
      {activeProgram && (
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={sectionLabel}>Den här veckan</div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>{activeDays.length} {activeDays.length === 1 ? 'pass' : 'pass'} loggade</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            {weekDays.map(d => {
              const ds = getDateStr(d);
              const done = activeDays.includes(ds);
              const isToday = ds === today;
              return (
                <div key={ds} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '999px', boxSizing: 'border-box', background: done ? brandAccent : 'transparent', border: done ? 'none' : isToday ? `2px solid ${brandAccent}` : `1px dashed ${NEUTRAL.line}` }} />
                  <span style={{ fontSize: '10px', color: isToday ? theme.accent_soft_text : NEUTRAL.muted, fontWeight: isToday ? '600' : '400' }}>{DAYS_SHORT[d.getDay()]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Senaste meddelandet */}
      {latestMsg && (
        <div onClick={() => handleTabChange('messages')} style={{ ...card, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          {!latestMsg.read && <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: brandAccent, flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink }}>Din terapeut skrev · <span style={{ fontWeight: '400', color: NEUTRAL.muted }}>{fmtMsgTime(latestMsg.created_at)}</span></div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>”{latestMsg.content}”</div>
          </div>
          <span aria-hidden="true" style={{ fontSize: '14px', color: NEUTRAL.line }}>›</span>
        </div>
      )}
    </div>
  );
}

export default ClientHomeTab;
